import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../queryKeys'
import { auditAiRequest } from '../../util/api/audit-ai-api'
import useStore from '../../util/store/auth'
import checkToken from '../../util/auth/checkToken'
import useDashboardListFilters from '../../util/useDashboardListFilters'
import { getDashboardQueryContext, getSortedMeasureIdsForQueryKey } from './dashboardHookUtils'
import type { DashboardGraphEventsResponse } from '@counsel-project/counsel-audit-ai-api'
import { getDashboardTimezone } from '../../util/dateUtils'

type UseDashboardGraphEventsOptions = {
  enabled?: boolean
  date?: string
  hour?: number
}

export const useDashboardGraphEvents = (options: UseDashboardGraphEventsOptions = {}) => {
  const { enabled = true, date: explicitDate, hour: explicitHour } = options
  const { directory } = useStore()
  const { filters } = useDashboardListFilters()
  const timezone = getDashboardTimezone()
  const {
    scopeFilters: { clinicianEmail, patientMrn, measureIds, location, program },
    selectedRangeDateHour: { date: fallbackDate, hour: fallbackHour },
  } = getDashboardQueryContext(filters, timezone)
  const noVisitDate = filters.noDateOfService || undefined
  const sortedMeasureIdsForQueryKey = getSortedMeasureIdsForQueryKey(measureIds)
  const date = explicitDate ?? fallbackDate
  const hour = explicitHour ?? fallbackHour
  const resolvedDate = date?.trim() ? date : undefined
  const resolvedHour =
    Number.isInteger(hour) && hour !== undefined && hour >= 0 && hour <= 23 ? hour : undefined

  const queryKey = queryKeys.dashboardGraphEvents(
    directory?._id ?? '',
    [
      resolvedDate,
      resolvedHour?.toString(),
      timezone,
      noVisitDate ? 'noVisitDate' : undefined,
      clinicianEmail,
      patientMrn,
      location,
      program,
      ...(sortedMeasureIdsForQueryKey ?? []),
    ].map((el) => el ?? '')
  )

  return useQuery<DashboardGraphEventsResponse['results']>({
    queryKey,
    queryFn: async () => {
      await checkToken()

      const eventsResponse = await auditAiRequest.dashboard.graphEvents({
        token: '',
        directoryId: directory?._id ?? '',
        date: resolvedDate ?? '',
        hour: resolvedHour ?? 0,
        timezone,
        noVisitDate,
        clinicianEmail: clinicianEmail ?? undefined,
        patientMrn: patientMrn ?? undefined,
        location,
        program,
        measureIds,
      })

      return eventsResponse.results
    },
    enabled: enabled && !!directory?._id && !!resolvedDate && resolvedHour !== undefined,
  })
}
